// Regras puras da biblioteca de Roteiros comerciais (sem tela, sem rede): filtro, validação, resumo e datas.
// Contrato do servidor: ações `rot_*` em /comercial/propostas-geradas (ver ./api).

export type StatusRoteiro = 'rascunho' | 'ativo' | 'arquivado';

export interface Roteiro {
  id: number;
  codigo: string;
  titulo: string;
  tipo: string;
  canal: string | null;
  objetivo: string | null;
  publico: string | null;
  /** Markdown. */
  conteudo: string;
  status: StatusRoteiro;
  ordem: number;
  versao: number;
  criado_em: string;
  criado_por: string | null;
  /** ISO com microssegundos: devolver igual em `visto.atualizado_em`. */
  atualizado_em: string;
  atualizado_por: string | null;
  letras: number;
}

export interface Rotulos {
  tipo: Record<string, string>;
  canal: Record<string, string>;
  status: Record<string, string>;
}

export interface Listagem {
  pode: { editar: boolean };
  roteiros: Roteiro[];
  rotulos: Rotulos;
}

export interface VersaoResumo {
  versao: number;
  titulo: string;
  status: string;
  gravado_em: string;
  gravado_por: string | null;
  letras: number;
}

export interface VersaoCompleta {
  versao: number;
  titulo: string;
  tipo: string;
  canal: string | null;
  objetivo: string | null;
  publico: string | null;
  conteudo: string;
  status: string;
  gravado_em: string;
  gravado_por: string | null;
}

/** O que o formulário de edição guarda (campos opcionais podem ficar vazios). */
export interface Formulario {
  titulo: string;
  tipo: string;
  canal: string;
  publico: string;
  objetivo: string;
  conteudo: string;
  status: string;
}

export interface Filtros {
  busca?: string;
  tipo?: string | null;
  canal?: string | null;
  status?: string | null;
}

// ── Listas e rótulos ─────────────────────────────────────────────────────────
export const TIPOS = ['reuniao', 'ligacao', 'mensagem', 'email', 'cadencia', 'outro'];
export const CANAIS = ['presencial', 'video', 'telefone', 'whatsapp', 'linkedin', 'email'];
export const STATUS = ['rascunho', 'ativo', 'arquivado'];

/** Rótulos usados até a resposta do servidor chegar (depois valem os de `rotulos`). */
export const ROTULOS: Rotulos = {
  tipo: { reuniao: 'Reunião', ligacao: 'Ligação', mensagem: 'Mensagem', email: 'E-mail', cadencia: 'Cadência', outro: 'Outro' },
  canal: { presencial: 'Presencial', video: 'Vídeo', telefone: 'Telefone', whatsapp: 'WhatsApp', linkedin: 'LinkedIn', email: 'E-mail' },
  status: { rascunho: 'Rascunho', ativo: 'Ativo', arquivado: 'Arquivado' },
};

export const LIMITES = { titulo: 160, objetivo: 600, publico: 300, conteudo: 60000 };

export const COR_DO_STATUS: Record<string, string> = { ativo: 'green', rascunho: 'gold', arquivado: 'default' };

export const rotuloDe = (grupo: Record<string, string>, codigo: string | null | undefined): string =>
  codigo ? grupo[codigo] ?? codigo : '';

// ── Texto ────────────────────────────────────────────────────────────────────
export function semAcento(t: string | null | undefined): string {
  return String(t ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Ordem do servidor: `ordem`, depois título (sem acento). Devolve uma cópia. */
export function ordenar(lista: Roteiro[]): Roteiro[] {
  return [...lista].sort((a, b) => (a.ordem - b.ordem) || semAcento(a.titulo).localeCompare(semAcento(b.titulo)));
}

/**
 * Busca sem acento e sem maiúscula (todas as palavras precisam aparecer) em título, objetivo, para quem e texto.
 * Sem `status`, o roteiro arquivado fica escondido; com `status`, só esse status.
 */
export function filtrarRoteiros(lista: Roteiro[], f: Filtros): Roteiro[] {
  const palavras = semAcento(f.busca).split(/\s+/).filter(Boolean);
  return lista.filter((r) => {
    if (f.status ? r.status !== f.status : r.status === 'arquivado') return false;
    if (f.tipo && r.tipo !== f.tipo) return false;
    if (f.canal && r.canal !== f.canal) return false;
    if (palavras.length) {
      const alvo = semAcento(`${r.titulo} ${r.objetivo ?? ''} ${r.publico ?? ''} ${r.conteudo}`);
      if (!palavras.every((w) => alvo.includes(w))) return false;
    }
    return true;
  });
}

/** Frases de erro (português) do formulário; lista vazia = pode salvar. */
export function validarFormulario(f: Partial<Formulario>): string[] {
  const erros: string[] = [];
  const titulo = (f.titulo ?? '').trim();
  if (!titulo) erros.push('Escreva o título do roteiro.');
  else if (titulo.length > LIMITES.titulo) erros.push(`O título passou de ${LIMITES.titulo} letras.`);
  if (!f.tipo || !TIPOS.includes(f.tipo)) erros.push('Escolha o tipo do roteiro.');
  if (f.canal && !CANAIS.includes(f.canal)) erros.push('Escolha um canal da lista ou deixe o canal vazio.');
  const conteudo = f.conteudo ?? '';
  if (!conteudo.trim()) erros.push('Escreva o texto do roteiro.');
  else if (conteudo.length > LIMITES.conteudo) erros.push('O texto passou de 60.000 letras.');
  if ((f.objetivo ?? '').length > LIMITES.objetivo) erros.push(`O objetivo passou de ${LIMITES.objetivo} letras.`);
  if ((f.publico ?? '').length > LIMITES.publico) erros.push(`O campo "Para quem" passou de ${LIMITES.publico} letras.`);
  return erros;
}

/** Primeiras letras do texto, sem os sinais do markdown, numa linha só. */
export function resumoDoTexto(conteudo: string | null | undefined, max = 160): string {
  const limpo = String(conteudo ?? '')
    .replace(/[#*>|`]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (limpo.length <= max) return limpo;
  return `${limpo.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

/** "dd/mm/aaaa hh:mm" no horário local. Texto que não é data volta como "—". */
export function fmtDataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dois = (n: number) => String(n).padStart(2, '0');
  return `${dois(d.getDate())}/${dois(d.getMonth() + 1)}/${d.getFullYear()} ${dois(d.getHours())}:${dois(d.getMinutes())}`;
}

export const formularioVazio = (): Formulario => ({
  titulo: '', tipo: 'mensagem', canal: '', publico: '', objetivo: '', conteudo: '', status: 'rascunho',
});

export const formularioDe = (r: Roteiro): Formulario => ({
  titulo: r.titulo, tipo: r.tipo, canal: r.canal ?? '', publico: r.publico ?? '', objetivo: r.objetivo ?? '',
  conteudo: r.conteudo, status: r.status,
});
